import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";

import type { ParsedAIEmail, ParsedAIEmailPreview } from "./types";

type ExtractionResult<T = unknown> = {
    /** The original string with the first JSON object removed (surrounding blank lines tidied). */
    cleaned: string;
    /** The parsed JSON object, or null if none found / not parseable. */
    json: T | null;
    /** The index in the original string where the JSON object started, or -1 if none found. */
    index: number;
};

const EMAIL_PREVIEWS_PREFIX = "```json_email_previews";
const EMAIL_PREFFIX = "```json_email";
const EMPTY_EMAIL: Readonly<ParsedAIEmail> = Object.freeze({ subject: "", body: "" });
const CHART_OBJECT_START_RE = /^\{\s*"chart"\s*:/;

const createParseJSONService = () => {
    const stripFenceArtifacts = (chunk: string): string => {
        if (!chunk) {
            return "";
        }
        return chunk.replace(/`{3,}/gi, "");
    };

    const findClosingQuote = (s: string, from: number): number => {
        let escaped = false;
        for (let i = from; i < s.length; i++) {
            const ch = s[i];
            if (escaped) {
                escaped = false;
                continue;
            }
            if (ch === "\\") {
                escaped = true;
                continue;
            }
            if (ch === '"') {
                return i;
            }
        }
        return -1;
    };

    const extractField = (source: string, fieldName: "subject" | "body"): string => {
        const keyRe = new RegExp(`"${fieldName}"\\s*:\\s*"`, "g");
        const m = keyRe.exec(source);
        if (!m) {
            return "";
        }
        const start = m.index + m[0].length;
        const end = findClosingQuote(source, start);
        return end === -1 ? source.slice(start) : source.slice(start, end);
    };

    const decodeBasic = (raw: string): string => {
        if (!raw) {
            return "";
        }
        return raw
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "\r")
            .replace(/\\t/g, "\t")
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, "\\");
    };

    const isLikeEmailPreviewJSON = (input: string): boolean => {
        return typeof input === "string" && input.trim().startsWith(EMAIL_PREVIEWS_PREFIX);
    };

    const isLikeEmailJSON = (input: string): boolean => {
        if (typeof input !== "string") {
            return false;
        }
        const trimmed = input.trim();

        if (trimmed.startsWith(EMAIL_PREFFIX)) {
            return true;
        }

        if (!trimmed.startsWith("{")) {
            return false;
        }
        if (
            trimmed.indexOf('"subject"') !== -1 &&
            trimmed.indexOf('"body"') !== -1 &&
            trimmed.indexOf('"chart"') !== -1
        ) {
            return true;
        }

        return false;
    };

    const parseEmail = (input: string): ParsedAIEmail => {
        if (!isLikeEmailJSON(input)) {
            return EMPTY_EMAIL;
        }

        const afterPrefix = input.startsWith(EMAIL_PREFFIX) ? input.slice(EMAIL_PREFFIX.length) : input;
        const stripped = stripFenceArtifacts(afterPrefix).trim();

        const subject = decodeBasic(extractField(stripped, "subject"));
        const body = decodeBasic(extractField(stripped, "body"));

        const email: ParsedAIEmail = { subject, body };
        return email;
    };

    const isEscaped = (s: string, idx: number): boolean => {
        let backslashes = 0;
        for (let k = idx - 1; k >= 0 && s[k] === "\\"; k--) {
            backslashes++;
        }
        return backslashes % 2 === 1;
    };

    const findJSONObjectRange = (s: string): { start: number; end: number } | null => {
        for (let i = 0; i < s.length; i++) {
            if (s[i] !== "{") {
                continue;
            }

            let depth = 0;
            let inString = false;
            let end = -1;

            for (let j = i; j < s.length; j++) {
                const c = s[j];

                if (inString) {
                    if (c === '"' && !isEscaped(s, j)) {
                        inString = false;
                    }
                    continue;
                }
                if (c === '"') {
                    inString = true;
                    continue;
                }

                if (c === "{") {
                    depth++;
                } else if (c === "}") {
                    depth--;
                    if (depth === 0) {
                        end = j;
                        break;
                    }
                }
            }

            if (end !== -1) {
                return { start: i, end };
            }
        }
        return null;
    };

    const isChartObjectStart = (s: string, start: number): boolean => CHART_OBJECT_START_RE.test(s.slice(start));

    const extractEmbeddedJSON = <T = unknown>(input: string): ExtractionResult<T> => {
        let offset = 0;

        while (offset < input.length) {
            const range = findJSONObjectRange(input.slice(offset));
            if (!range) {
                break;
            }

            const start = offset + range.start;
            const end = offset + range.end;
            const jsonText = input.slice(start, end + 1);

            if (!isChartObjectStart(input, start)) {
                offset = end + 1;
                continue;
            }

            try {
                const parsed = JSON.parse(jsonText) as T;
                const before = input.slice(0, start);
                const after = input.slice(end + 1);
                const cleaned = before + after;

                return { cleaned, json: parsed, index: before.length };
            } catch {
                offset = end + 1;
            }
        }

        return { cleaned: input, json: null, index: -1 };
    };

    const parseAIEmailPreviews = (input: string): ParsedAIEmailPreview[] => {
        if (!isLikeEmailPreviewJSON(input)) {
            return [];
        }

        // 1) Remove the fixed prefix and strip any remaining fence artifacts (e.g., trailing ```).
        const afterPrefix = input.slice(EMAIL_PREVIEWS_PREFIX.length);
        const stripped = stripFenceArtifacts(afterPrefix).trim();

        // 2) Bound the payload to the JSON array block.
        const start = stripped.indexOf("[");
        const end = stripped.lastIndexOf("]");
        if (start === -1 || end === -1 || end <= start) {
            return [];
        }

        // We'll scan this slice, extracting one JSON object { ... } at a time.
        let rest = stripped.slice(start, end + 1);

        const previews: ParsedAIEmailPreview[] = [];

        // 3) Iterate object-by-object using the service's JSON object range finder.
        while (true) {
            const range = findJSONObjectRange(rest);
            if (!range) {
                break;
            }

            const objText = rest.slice(range.start, range.end + 1);

            // 4) Extract fields within THIS object using the service's quote-safe extractor.
            const rawSubject = extractField(objText, "subject");
            const rawBody = extractField(objText, "body");

            // 5) Decode escaped sequences consistently with the rest of the service.
            const subject = decodeBasic(rawSubject ?? "");
            const bodyRaw = decodeBasic(rawBody ?? "");

            // 6) Pull out embedded chart JSON (if any) and its anchor index in the cleaned body.
            const { cleaned, json, index } = extractEmbeddedJSON<Record<string, unknown>>(bodyRaw);

            previews.push({
                subject,
                body: cleaned, // body with embedded JSON removed
                chartJson: json, // parsed chart object or null
                chartAnchorIndex: index, // insertion point in the cleaned body
            });

            // 7) Advance past this object and continue scanning for the next one.
            rest = rest.slice(range.end + 1);
        }

        return previews;
    };

    const stableStringify = (obj: Record<string, unknown>) => JSON.stringify(obj, Object.keys(obj).sort());

    const areSamePreview = (a?: ParsedAIEmailPreview, b?: ParsedAIEmailPreview) => {
        if (!a || !b) {
            return false;
        }
        const ai = a.chartAnchorIndex ?? -1;
        const bi = b.chartAnchorIndex ?? -1;
        return (
            a.subject === b.subject &&
            a.body === b.body &&
            ai === bi &&
            !!a.chartJson &&
            !!b.chartJson &&
            stableStringify(a.chartJson) === stableStringify(b.chartJson)
        );
    };

    const areSameJSON = (a?: Record<string, unknown> | null, b?: Record<string, unknown> | null) => {
        if (a === b) {
            return true;
        }
        if (!a || !b) {
            return false;
        }
        return stableStringify(a) === stableStringify(b);
    };

    const isNonEmptyString = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;

    const isCompetitor = (v: unknown): v is Competitor => {
        if (typeof v !== "object" || !v) {
            return false;
        }
        const o = v as Record<string, unknown>;
        if (!isNonEmptyString(o.domain)) {
            return false;
        }
        if (o.companyName != null && typeof o.companyName !== "string") {
            return false;
        }
        if (o.favicon != null && typeof o.favicon !== "string") {
            return false;
        }
        return true;
    };

    const parseCompetitors = (input: string): Competitor[] => {
        if (typeof input !== "string" || !input.trim()) {
            return [];
        }

        const stripped = stripFenceArtifacts(input).trim();

        const tryParse = (text: string): unknown => {
            try {
                return JSON.parse(text);
            } catch {
                return undefined;
            }
        };

        const payload: unknown = tryParse(stripped);

        if (!Array.isArray(payload)) {
            return [];
        }

        const result: Competitor[] = [];
        payload.forEach((item) => {
            if (!isCompetitor(item)) {
                return;
            }
            const competitor: Competitor = {
                domain: item.domain.trim(),
                companyName: item.companyName?.trim(),
                favicon: item.favicon?.trim(),
            };
            result.push(competitor);
        });

        return result;
    };

    return {
        parseEmail,
        parseAIEmailPreviews,
        extractEmbeddedJSON,
        areSamePreview,
        areSameJSON,
        parseCompetitors,
        isLikeEmailPreviewJSON,
        isLikeEmailJSON,
    };
};

const ParseJSONService = createParseJSONService();

export default ParseJSONService;
